import { useState, useEffect } from "react";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { LOGO_URL } from "../lib/data";

const links = [
  { href: "#home", label: "Home" },
  { href: "#services", label: "Services" },
  { href: "#industries", label: "Industries" },
  { href: "#about", label: "About" },
  { href: "#contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [active, setActive] = useState("home");

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 8);
      const ids = ["home", "services", "industries", "about", "contact", "process"];
      let current = "home";
      for (const id of ids) {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top <= 140) current = id;
      }
      setActive(current);
    };
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-navy/10 shadow-[0_4px_24px_rgba(15,61,110,0.06)]"
          : "bg-white/60 backdrop-blur-md border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 flex items-center justify-between h-20">
        <a href="#home" className="flex items-center gap-3" data-testid="navbar-logo-link">
          <img src={LOGO_URL} alt="Insafe Services" className="h-12 w-12 object-contain" />
          <div className="leading-tight hidden sm:block">
            <div className="font-heading font-bold text-navy text-lg tracking-tight">INSAFE</div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-steel-dark">Services Pvt. Ltd.</div>
          </div>
        </a>

        <nav className="hidden lg:flex items-center gap-1">
          {links.map((l) => {
            const id = l.href.slice(1);
            const isActive = active === id;
            return (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={`px-4 py-2 text-sm font-medium tracking-wide transition-colors rounded-md ${
                  isActive ? "text-navy" : "text-slate-600 hover:text-navy"
                }`}
              >
                {l.label}
              </a>
            );
          })}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href="#contact"
            data-testid="navbar-cta-request"
            className="hidden md:inline-flex items-center gap-2 bg-navy text-white px-5 py-2.5 rounded-full text-sm font-medium btn-lift hover:bg-navy-deep"
          >
            Request Audit <ArrowUpRight className="h-4 w-4" />
          </a>
          <button
            data-testid="navbar-mobile-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden p-2 rounded-md text-navy"
            aria-label="Toggle menu"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-navy/10" data-testid="navbar-mobile-menu">
          <nav className="px-6 py-4 flex flex-col gap-1">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                className="px-3 py-3 text-base font-medium rounded-md text-slate-700 hover:bg-steel-light hover:text-navy"
              >
                {l.label}
              </a>
            ))}
            <a
              href="#contact"
              onClick={() => setOpen(false)}
              data-testid="mobile-cta-request"
              className="mt-2 inline-flex items-center justify-center gap-2 bg-navy text-white px-5 py-3 rounded-full text-sm font-medium"
            >
              Request Audit <ArrowUpRight className="h-4 w-4" />
            </a>
          </nav>
        </div>
      )}
    </header>
  );
}
